import { AfterViewInit, Component, ElementRef, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { SavedIlsService } from '../shared/services/savedIls.service'
import { TokenService } from '@monorepo/auth'
import { catchError, throwError } from 'rxjs'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { Title } from '@angular/platform-browser'
import { SendLogActionService } from '../shared/services/send-log-action.service'
import { IlsModelService } from '../shared/services/ils-model.service'
import { IlsDescription, IlsService, StudentToken } from '@monorepo/golab-generated'
import { TitleBarComponent } from '../ils-player/title-bar/title-bar.component'
import { SanitizeHtmlPipe } from '@monorepo/angular-common'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { FormsModule } from '@angular/forms'
import { StudentGroupContentService } from '../shared/services/student-group-content.service'

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TitleBarComponent, TranslatePipe, SanitizeHtmlPipe, MatIconButton, MatIcon, MatTooltip, FormsModule],
})
export class LoginComponent implements AfterViewInit {
  private readonly router = inject(Router);
  private readonly savedIlsService = inject(SavedIlsService);
  private readonly sendLogActionService = inject(SendLogActionService);
  private readonly ilsModelService = inject(IlsModelService);
  private readonly ilsService = inject(IlsService);
  private readonly translateService = inject(TranslateService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly tokenService = inject(TokenService);
  private readonly titleService = inject(Title);
  private readonly studentGroupContentService = inject(StudentGroupContentService);

  nickname: string = ''
  ilsId: string = ''
  ilsDescription: IlsDescription | undefined
  @ViewChild('nicknameInput') nicknameInput: ElementRef | undefined
  previewMode: boolean = false

  constructor() {
    this.activatedRoute.paramMap.subscribe((params) => {
      if (params?.has('ilsId')) {
        this.ilsId = this.getIlsIdFromUrl(params.get('ilsId') ?? '')
        this.updateIlsDescription(this.ilsId)
      }
    })
    this.ilsModelService.loggedOut()
    this.studentGroupContentService.clearStudentGroupContent()
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.nicknameInput?.nativeElement.focus()
    }, 0)
  }

  validNickname(): boolean {
    return this.nickname.trim().length > 0
  }

  loginClicked() {
    if (!this.validNickname()) {
      return
    }
    this.ilsService
      .ilsControllerStudentLogin({
        ilsId: this.ilsId,
        body: {
          nickname: this.nickname,
        },
      })
      .subscribe((student: StudentToken) => {
        this.tokenService.saveAccessToken(student.accessToken)
        if (!this.previewMode) {
          this.sendLogActionService.sendLoginAction()
        }
        this.router.navigate([this.getPlayUrl()])
      })
  }

  enterClicked(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.loginClicked()
    }
  }

  private getPlayUrl(): string {
    const previousUrl = this.savedIlsService.previousUrl$.value ?? ''
    if (previousUrl) {
      return this.getIlsIdFromUrl(previousUrl)
    }
    return previousUrl
  }

  private getIlsIdFromUrl(url: string): string {
    const [ilsId] = url.split('?')
    return ilsId
  }

  private updateIlsDescription(ilsId: string | null) {
    if (!ilsId) {
      console.warn('ilsId is null')
      return
    }
    this.ilsService
      .ilsControllerFindIlsDescription({ ilsId })
      .pipe(
        catchError((err) => {
          if (err.status === 404) {
            console.log("Description wasn't found - redirecting to error page")
            this.savedIlsService.clearSavedUrl()
            this.router.navigate(['/error'], { relativeTo: this.activatedRoute.parent })
          }

          if (err.status === 500) {
            console.log('Internal server error. Redirecting to error page')
            this.savedIlsService.clearSavedUrl()
            this.router.navigate(['/error'], { relativeTo: this.activatedRoute.parent })
          }
          return throwError(err)
        }),
      )
      .subscribe((ilsDescription: IlsDescription) => {
        this.ilsDescription = ilsDescription
        this.translateService.use(ilsDescription.language)
        this.ilsModelService.loadInfoFromIlsDescription(ilsDescription)
        this.previewMode = !!ilsDescription.publishedAt
        const ilsPlayerLabel = this.translateService.instant('twenteIlsPlayer')
        this.titleService.setTitle(`${this.ilsModelService.serverPreTitle}${ilsPlayerLabel}: ${ilsDescription.title}`)
        document.documentElement.lang = this.ilsModelService.language$.value
        // console.log('ilsDescription', ilsDescription)
      })
  }
}
